import { IResolvers, TypeSource } from '@graphql-tools/utils';
import { makeExecutableSchema, scalarTypeDefs } from '@spazious/dollhouse-graphql';
import {
  ApolloClient,
  DefaultContext,
  fromPromise,
  InMemoryCache,
  onError,
  SchemaLink,
  setContext,
} from '@spazious/dollhouse-graphql/apollo';

export const createContextLink = <Context extends DefaultContext>(context: Context) => {
  return setContext(() => {
    return context;
  });
};

export interface CreateApolloClientOptions {
  /**
   * Called when an operation fails with a 401/unauthenticated error. When the
   * returned promise resolves the failed operation is retried once.
   */
  onUnauthorized?: () => Promise<unknown>;
}

/**
 * Returns true when the given error represents a 401/unauthenticated failure.
 */
export const isUnauthorizedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const { status, statusCode, response, extensions } = error as {
    status?: number;
    statusCode?: number;
    response?: { status?: number };
    extensions?: { code?: string };
  };
  return (
    status === 401 ||
    statusCode === 401 ||
    response?.status === 401 ||
    extensions?.code === 'UNAUTHENTICATED'
  );
};

/**
 * Creates an Apollo client with the provided API client.
 */
export const createApolloClient = <TContext extends DefaultContext>(
  context: TContext,
  typeDefs: TypeSource,
  resolvers: IResolvers<any, TContext> | Array<IResolvers<any, TContext>>,
  options?: CreateApolloClientOptions
) => {
  const ctxLink = createContextLink(context);
  const schema = makeExecutableSchema({
    typeDefs: [scalarTypeDefs, typeDefs],
    resolvers,
  });

  const apolloCache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          virtualObjects: {
            keyArgs: ['spaceId', 'type'],
            merge(existing, incoming) {
              return {
                ...incoming,
                data: [...(existing?.data ?? []), ...(incoming.data ?? [])],
              };
            },
          },
        },
      },
    },
  });

  const graphqlClient = new ApolloClient({
    cache: apolloCache,
    link: onError(({ graphQLErrors, networkError, operation, forward }) => {
      graphQLErrors?.forEach(({ originalError }) => {
        originalError && console.error('GraphQL error:', originalError);
      });
      networkError && console.error('Network error:', networkError);

      const onUnauthorized = options?.onUnauthorized;
      if (!onUnauthorized) return;

      const unauthorized =
        isUnauthorizedError(networkError) ||
        (graphQLErrors ?? []).some(
          (error) => isUnauthorizedError(error) || isUnauthorizedError(error.originalError)
        );

      if (!unauthorized || operation.getContext().unauthorizedRetried) return;

      // Refresh the credentials, then retry the failed operation once.
      return fromPromise(onUnauthorized()).flatMap(() => {
        operation.setContext({ unauthorizedRetried: true });
        return forward(operation);
      });
    }).concat(
      ctxLink.concat(
        new SchemaLink({
          schema,
          context: context,
        })
      )
    ),
  });

  return graphqlClient;
};
